import { Component, Inject, OnInit } from "@angular/core";
import { FormArray, FormBuilder, FormGroup, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import * as moment from "moment";
import { ToastrService } from "ngx-toastr";
import { AdminService } from "src/app/admin/admin.service";

interface DialogData {
  currency: string;
  chatID: string;
  vendorOrderId: number;
  customerName: string;
  sessionCount: number;
  batchId: number;
}

export enum Stages {
  Inspection = 6,
}

@Component({
  selector: "app-schedule-inspection",
  templateUrl: "./schedule-inspection.component.html",
  styleUrls: ["./schedule-inspection.component.scss"],
})
export class ScheduleInspectionComponent implements OnInit {
  inspectionForm: FormGroup;
  checklistForm: FormGroup = this.formBuilder.group({
    checkListValue: [""],
  });

  checkLists: any = {};
  today: Date = new Date();
  vendorOrderId: any;
  batches: any[] = [];
  inspectionRoles: any[];
  batchParts: any[] = [];
  selectedBatches: any[] = [];
  customerName: any;
  sessionCount: any;
  chatID: any;
  processTrackerBatchId?: number;
  minTime: Date;

  constructor(
    public dialogref: MatDialogRef<ScheduleInspectionComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogData,
    private adminService: AdminService,
    private toastr: ToastrService,
    private formBuilder: FormBuilder
  ) {
    moment.locale('en-US'); // Ensure 12-hour format
    this.vendorOrderId = data.vendorOrderId;
    this.customerName = data.customerName;
    this.sessionCount = data.sessionCount;
    this.chatID = data.chatID;
    this.processTrackerBatchId = data.batchId;
  }

  ngOnInit(): void {
    this.initializeForm();
    this.getOrderBatches();
    this.getInspectionPermissionRoles();
    this.updateMinTime();
    // setInterval(() => this.updateMinTime(), 60000);
  }

  updateMinTime() {
    const now = moment().add(15, 'minutes');
    this.minTime = now.toDate();
    const currentTime = this.inspectionForm.get('inspectionTime').value;
    if (currentTime) {
      const selectedTime = moment(currentTime, 'h:mm A');
      if (selectedTime.isBefore(now)) {
        this.inspectionForm.get('inspectionTime').setValue(now.format('h:mm A'));
      }
    }
  }

  // onTimeChange(event: any) {
  //   if (event.value) {
  //     const timeStr = moment(event.value).format('h:mm A');
  //     this.inspectionForm.get('inspectionTime').setValue(timeStr);
  //   }
  // }

  initializeForm() {
    this.inspectionForm = this.formBuilder.group({
      inspectionDate: ["", [Validators.required]],
      inspectionTime: ["", [Validators.required]],
      attendees: this.formBuilder.array(
        [
          this.formBuilder.group({
            email: [localStorage.getItem("email"), Validators.email],
            roleId: [null],
          }),
        ],
        Validators.required
      ),
    });
  }

  getOrderBatches() {
    this.adminService
      .getOrderBatches(this.vendorOrderId)
      .subscribe((res: any) => {
        this.batches = this.processTrackerBatchId
          ? res.filter((batch) => batch?._id === this.processTrackerBatchId)
          : res.filter(
            (batch) => batch?.currentStage?._id === Stages?.Inspection
          );

        if (this.batches?.length === 0) {
          this.toastr.warning(
            "Currently no batches available for inspection",
            "Warning"
          );
        }
        const batchPartsDetails = this.batches.map((item) => {
          // this.ngZone.run(() => {
          if (!this.checkLists[item?.vendorOrderItemId]) {
            this.checkLists[item?.vendorOrderItemId] = [];
          }
          // if (this.checkLists[item?.vendorOrderItemId]) {
          item?.vendorOrderItem?.quality_control_params?.forEach((qcp) => {
            if (qcp?.quality_checklist) {
              this.checkLists[item?.vendorOrderItemId].push({
                title: qcp?.quality_checklist,
              });
            }
          });
          // }
          // });

          return {
            partName: item?.vendorOrderItem?.model_attributes?.file_name,
            partId: item?.vendorOrderItemId,
            technologyName: item?.vendorOrderItem?.technology_name,
          };
        });

        const uniqueIds = new Set(batchPartsDetails.map((item) => item.partId));
        this.batchParts = Array.from(uniqueIds).map((id) =>
          batchPartsDetails.find((item) => item.partId === id)
        );
      });
  }

  getInspectionPermissionRoles() {
    this.adminService.getInspectionSessionRoles().subscribe((res: any) => {
      console.log(res);
      this.inspectionRoles = res;
    });
  }

  get attendeesArray() {
    return this.inspectionForm.get("attendees") as FormArray;
  }

  addNewAttendeeBatch() {
    return this.attendeesArray.push(this.createAttendeeItems());
  }

  createAttendeeItems() {
    return this.formBuilder.group({
      email: [""],
      roleId: [null],
    });
  }

  removeAttendeeItem(index: number) {
    this.attendeesArray.removeAt(index);
  }

  getRoleDescription(index: number) {
    const roleId = this.attendeesArray?.value[index]?.roleId;
    return this.inspectionRoles?.filter((item) => item?.id === roleId)[0]
      ?.description;
  }

  addChecklistItem(partId: number) {
    const value = this.checklistForm.value.checkListValue;
    if (!value) {
      this.toastr.error("Please type in your checklist title", "Error");
      return;
    }
    const tempObj = {
      title: value,
    };
    if (this.checkLists[partId]) {
      this.checkLists[partId].push(tempObj);
    } else {
      this.checkLists[partId] = [tempObj];
    }
    this.checklistForm.reset();
  }

  removeCheckListItem(partId: number, checkListId: number) {
    const index = this.checkLists[partId].findIndex(
      (doc) => doc.id === checkListId
    );
    if (index !== -1) {
      this.checkLists[partId].splice(index, 1);
    }
  }

  onBatchSelectionChange(event: any, item: any) {
    if (event.checked === true) {
      this.selectedBatches.push(item?._id);
    } else if (event.checked === false) {
      this.selectedBatches.splice(this.selectedBatches.indexOf(item?._id), 1);
    }
  }

  sendInvite() {
    const requestBody = {
      date: this.getUtcDateWithTime(this.inspectionForm.get("inspectionDate").value),
      // data: this.getUtcDateTime(),
      time: this.inspectionForm.get("inspectionTime").value,
      chatId: this.chatID,
      timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      batches: this.selectedBatches,
      checklists: this.checkLists,
      attendees: this.inspectionForm.get("attendees").value,
    };
    console.log(requestBody);
    this.adminService
      .sendInspectionRequest(this.vendorOrderId, requestBody)
      .subscribe(
        (res) => {
          if (res) {
            this.toastr.success(
              "Inspection scheduled successfully.",
              "Success"
            );
            this.close(true);
          }
        },
        (err) => {
          this.toastr.error(
            "Could not schedule inspection session now.",
            "Error"
          );
          this.close(false);
        }
      );
  }

  close(isSuccess?: boolean) {
    this.dialogref.close(isSuccess);
  }

  // getUtcDateWithTime(date: any) {
  //   const normalizedDate = new Date(date); // Angular Material returns the date in local time
  //   const year = normalizedDate.getFullYear();
  //   const month = normalizedDate.getMonth(); // Month is 0-indexed
  //   const day = normalizedDate.getDate();
  //   // const utcDate = new Date(Date.UTC(year, month, day));
  //   const [hours, minutes] = this.inspectionForm.get("inspectionTime").value.split(':').map(Number);

  //   // Create a combined date-time object in the user's local time
  //   const combinedDateTime = new Date(year, month, day, hours, minutes);
  //   console.log(normalizedDate);
  //   console.log(combinedDateTime);
  //   return combinedDateTime;

  // }
  getUtcDateWithTime(date: any) {
    const normalizedDate = new Date(date);
    const year = normalizedDate.getFullYear();
    const month = normalizedDate.getMonth();
    const day = normalizedDate.getDate();

    const timeStr = this.inspectionForm.get('inspectionTime').value;
    const parsedTime = moment(timeStr, 'h:mm A');
    const hours = parsedTime.hours();
    const minutes = parsedTime.minutes();

    const combinedDateTime = new Date(year, month, day, hours, minutes);
    // return new Date(Date.UTC(
    //   combinedDateTime.getFullYear(),
    //   combinedDateTime.getMonth(),
    //   combinedDateTime.getDate(),
    //   combinedDateTime.getHours(),
    //   combinedDateTime.getMinutes()
    // ));
    return new Date(
      combinedDateTime.getFullYear(),
      combinedDateTime.getMonth(),
      combinedDateTime.getDate(),
      combinedDateTime.getHours(),
      combinedDateTime.getMinutes()
    )
    
  }
}
